"use client";
import { useCallback } from "react";
import { useSetRecoilState } from "recoil";

import { Confirm, Error, Info, Notification, modalStore } from "store/modalStore";

export const useModal = () => {
  const setModal = useSetRecoilState(modalStore);

  const closeModal = useCallback(() => {
    setModal({
      info: { modalState: "close" },
      notification: { modalState: "close" },
      confirm: { modalState: "close" },
      dynamicHeightConfirm: { modalState: "close" },
      error: { modalState: "close" },
    });
  }, [setModal]);

  const openNotificationModal = useCallback(
    (props: Omit<Notification, "modalState">) => {
      setModal({
        notification: {
          modalState: "open",
          notificationMessage: props?.notificationMessage,
          isHiddenWarningIcon: props?.isHiddenWarningIcon ?? false,
          subMessage: props?.subMessage,
          positiveButtonText: props?.positiveButtonText,
          positiveAction: props?.positiveAction,
          cancelAction: props?.cancelAction,
        },
      });
    },
    [setModal]
  );

  const openConfirmModal = useCallback(
    (props: Omit<Confirm, "modalState">) => {
      setModal({
        confirm: {
          modalState: "open",
          confirmMessage: props?.confirmMessage,
          subMessage: props?.subMessage,
          positiveButtonText: props?.positiveButtonText,
          positiveAction: props?.positiveAction,
          cancelAction: props.cancelAction,
          top: props.top,
        },
      });
    },
    [setModal]
  );

  const openDynamicHeightConfirmModal = useCallback(
    (props: Omit<Confirm, "modalState">) => {
      setModal({
        dynamicHeightConfirm: {
          modalState: "open",
          confirmMessage: props?.confirmMessage,
          subMessage: props?.subMessage,
          positiveButtonText: props?.positiveButtonText,
          positiveAction: props?.positiveAction,
          cancelAction: props.cancelAction,
          top: props.top,
        },
      });
    },
    [setModal]
  );

  const openInfoModal = useCallback(
    (props: Omit<Info, "modalState">) => {
      setModal({
        info: {
          modalState: "open",
          infoMessage: props?.infoMessage,
          subMessage: props?.subMessage,
          onClose: props?.onClose,
        },
      });
    },
    [setModal]
  );

  const openErrorModal = useCallback(
    (props: Omit<Error, "modalState">) => {
      setModal({
        error: {
          modalState: "open",
          errorMessage: props.errorMessage,
          subMessage: props.subMessage,
          attentionMessage: props.attentionMessage,
          buttonText: props.buttonText,
          onClose: props?.onClose,
        },
      });
    },
    [setModal]
  );

  return { closeModal, openNotificationModal, openConfirmModal, openDynamicHeightConfirmModal, openInfoModal, openErrorModal };
};
